import React, { FC, ReactNode, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { ZodObject } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Dialog, Input } from '../../ui'
import { InputProps } from '../../ui/Input'

interface InputModalProps {
	isOpen: boolean
	onClose: () => void
	onCancel?: () => void
	title: string
	icon?: ReactNode
	onAction: (newName: string) => Promise<void>
	inputLabel?: ReactNode | string
	inputPlaceholder?: string
	defaultValue?: string
	cancelLabel?: string
	actionLabel?: string
	loading?: boolean
	maxLength?: number
	zodValidationSchema?: ZodObject<any>
	inputProps?: InputProps
}

const InputModal: FC<InputModalProps> = ({
	isOpen,
	onClose,
	onCancel,
	defaultValue,
	title,
	icon,
	onAction,
	inputLabel,
	inputPlaceholder,
	cancelLabel = 'Cancel',
	actionLabel = 'Save',
	loading,
	maxLength,
	zodValidationSchema,
	inputProps,
}) => {
	const {
		control,
		watch,
		reset,
		formState: { errors, isDirty },
		handleSubmit,
	} = useForm({
		resolver: zodValidationSchema
			? zodResolver(zodValidationSchema)
			: undefined,
		defaultValues: {
			name: defaultValue ?? '',
		},
	})

	const { name } = watch()
	useEffect(() => {
		if (defaultValue) {
			reset({ name: defaultValue })
		}
	}, [defaultValue, reset])

	const handleClose = () => {
		onClose()
		reset({ name: '' })
	}

	useEffect(() => {
		if (!isOpen) {
			handleClose()
		}
	}, [isOpen])

	if (!isOpen) return null

	return (
		<Dialog
			isOpen={isOpen}
			onClose={handleClose}
			isDirty={isDirty}
			title={title}
			icon={icon}
			controllers={
				<div className="flex items-center justify-end w-full gap-2">
					<Button
						variant="tertiary"
						label={cancelLabel ?? 'Cancel'}
						onClick={onCancel ?? handleClose}
						disabled={loading}
					/>
					<Button
						loading={loading}
						label={actionLabel ?? 'Save'}
						onClick={handleSubmit(async (data) => {
							await onAction(data?.name?.trim())
						})}
						disabled={!!errors.name || !name?.trim()}
					/>
				</div>
			}
		>
			<div className="max-w-[500px] md:w-[500px]">
				{!!(typeof inputLabel !== 'string') && (
					<p className="mb-2 text-base text-text-main dark:text-text-main-dark">
						{inputLabel}
					</p>
				)}

				<Controller
					name="name"
					control={control}
					render={({ field }) => (
						<Input
							label={typeof inputLabel === 'string' ? inputLabel : ''}
							labelClassName={'mb-2 text-base text-text-main'}
							error={errors.name?.message}
							value={field.value}
							onChange={(e) => field.onChange(e)}
							inputClassName="px-5 h-12 rounded-[10px] dark:text-text-main-dark"
							maxLength={maxLength}
							placeholder={inputPlaceholder}
							onEnterClick={handleSubmit(async (data) => {
								console.log('click', !!errors.name, !name?.trim())
								if (!!errors.name || !name?.trim()) return
								await onAction(data?.name?.trim())
							})}
							{...inputProps}
						/>
					)}
				/>
			</div>
		</Dialog>
	)
}

export default InputModal
